/** A click/keyboard disclosure; product choices are rendered by one shared Liquid snippet. */
class AvlNavigation extends HTMLElement {
  #abort;
  #panel;
  #trigger;
  #generation = 0;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#panel = this.querySelector('[data-avlc-nav-panel]');
    if (!this.#panel) return;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('pointerdown', this.#onOutside, { signal });
    window.addEventListener('avl:drawer-open', this.#onDrawerOpen, { signal });
    window.addEventListener('resize', this.#onResize, { signal });
  }

  disconnectedCallback() {
    this.#generation++;
    this.#abort?.abort();
  }

  get isOpen() { return Boolean(this.#panel && !this.#panel.hidden); }

  async open(trigger, focusFirst = false) {
    const generation = ++this.#generation;
    this.#trigger = trigger;
    const detail = { kind: 'menu', source: this, waits: [] };
    window.dispatchEvent(new CustomEvent('avl:drawer-open', { detail }));
    await Promise.all(detail.waits);
    if (!this.isConnected || generation !== this.#generation) return;
    this.#panel.hidden = false;
    this.querySelectorAll('[data-avlc-nav-trigger]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    if (focusFirst) this.#panel.querySelector('a[href], button')?.focus();
  }

  close({ restoreFocus = false } = {}) {
    this.#generation++;
    if (!this.#panel) return;
    this.#panel.hidden = true;
    this.querySelectorAll('[data-avlc-nav-trigger]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    if (restoreFocus) this.#trigger?.focus();
  }

  #onClick = (event) => {
    const link = event.target.closest('a[href]');
    if (link && this.#panel?.contains(link) && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && link.target !== '_blank') {
      event.preventDefault();
      window.location.assign(link.href);
      return;
    }
    const trigger = event.target.closest('[data-avlc-nav-trigger]');
    if (trigger) {
      event.preventDefault();
      this.isOpen ? this.close() : this.open(trigger);
    } else if (event.target.closest('[data-avlc-nav-close]')) {
      this.close({ restoreFocus: true });
    }
  };

  #onKeyDown = (event) => {
    const trigger = event.target.closest('[data-avlc-nav-trigger]');
    if (trigger && event.key === 'ArrowDown') {
      event.preventDefault();
      this.open(trigger, true);
    } else if (trigger && this.isOpen && event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      this.#panel.querySelector('a[href], button')?.focus();
    } else if (this.isOpen && event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close({ restoreFocus: true });
    }
  };

  #onFocusOut = (event) => {
    // Only close when focus actually moves to another focusable element outside the menu.
    // A mouse click on a link (Safari does not focus links) must not close the panel mid-click.
    const next = event.relatedTarget;
    if (!next) return;
    queueMicrotask(() => { if (this.isOpen && !this.contains(next)) this.close(); });
  };
  #onOutside = (event) => { if (!this.contains(event.target)) this.close(); };
  #onDrawerOpen = (event) => { if (event.detail?.source !== this) this.close(); };
  #onResize = () => {
    if (!this.getClientRects().length || this.closest('header-component')?.dataset.menuStyle === 'drawer') this.close();
  };
}

if (!customElements.get('avl-navigation')) customElements.define('avl-navigation', AvlNavigation);
